import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";

const RoleSelect = ({ value, onChange, id, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="w-40" data-testid={id}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="technician">Technicien</SelectItem>
      <SelectItem value="admin">Administrateur</SelectItem>
    </SelectContent>
  </Select>
);

const EMPTY = { name: "", email: "", password: "", role: "technician" };

export default function Technicians() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => setUsers((await api.get("/users")).data), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/users", form), "Compte technicien créé")) setForm(EMPTY);
  };
  const resetPw = (u) => {
    const pw = window.prompt(`Nouveau mot de passe pour ${u.name} (8 caractères min.)`);
    if (pw) run(() => api.patch(`/users/${u.user_id}`, { password: pw }), "Mot de passe modifié");
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="flex-1 overflow-y-auto px-6 lg:px-10 py-8 max-w-5xl">
      <PageHeader kicker="ÉQUIPE" title="Techniciens" />
      <form onSubmit={add} className="rounded-lg border border-slate-200 bg-white p-6 mb-8" data-testid="add-tech-form">
        <p className="text-sm text-slate-500 mb-4">Créez le compte d'un technicien. Communiquez-lui son e-mail et son mot de passe : il aura accès à toutes les machines de vos clients.</p>
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <Input data-testid="tech-name-input" required placeholder="Nom" value={form.name} onChange={set("name")} />
          <Input data-testid="tech-email-input" type="email" required placeholder="technicien@exemple.com" value={form.email} onChange={set("email")} />
          <Input data-testid="tech-password-input" type="text" required minLength={8} placeholder="Mot de passe (8 caractères min.)" value={form.password} onChange={set("password")} />
          <RoleSelect value={form.role} onChange={(r) => setForm({ ...form, role: r })} id="tech-role-select" />
        </div>
        <Button data-testid="tech-submit-btn" type="submit"><UserPlus className="h-4 w-4 mr-1.5" /> Créer le compte</Button>
      </form>
      <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-200" data-testid="users-list">
        {users.map((u) => (
          <li key={u.user_id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`user-row-${u.user_id}`}>
            <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold">{u.name[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{u.name}{u.user_id === me.user_id && <span className="text-slate-500 font-normal"> (vous)</span>}</p>
              <p className="text-xs text-slate-500 truncate">{u.email} · depuis le {fmtDate(u.created_at)}</p>
            </div>
            <RoleSelect value={u.role} id={`user-role-${u.user_id}`} disabled={u.user_id === me.user_id} onChange={(r) => run(() => api.patch(`/users/${u.user_id}`, { role: r }), "Rôle mis à jour")} />
            <button data-testid={`user-reset-pw-${u.user_id}`} onClick={() => resetPw(u)} className="text-slate-500 hover:text-blue-600" title="Changer le mot de passe">
              <KeyRound className="h-4 w-4" />
            </button>
            {u.user_id !== me.user_id && (
              <button data-testid={`user-delete-${u.user_id}`} onClick={() => window.confirm("Supprimer ce compte ?") && run(() => api.delete(`/users/${u.user_id}`), "Compte supprimé")} className="text-slate-500 hover:text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
